import { ActionIcon, type BoxProps, Button, Flex, Group, type MantineBreakpoint, SimpleGrid } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useToggle } from "@mantine/hooks";
import { IconFilter, IconFilterX } from "@tabler/icons-react";
import { cloneElement, type ReactElement } from "react";

type FilterSectionProps<F> = {
  filters: ReactElement<BoxProps>[];
  form: UseFormReturnType<F>;
  onSubmit: (values: F) => void;
  onReset: () => void;
};

const amountOfRowsVisible = 2;
const cols: Record<MantineBreakpoint, number> = {
  base: 3,
  xs: 3,
  sm: 3,
  md: 3,
  lg: 4,
  xl: 6,
};

const orderedColEntries = Object.entries(cols).sort((a, b) => a[0].localeCompare(b[0]));

const minFiltersForExpansion = orderedColEntries[0][1] * amountOfRowsVisible - 1;

const filterVisibleFrom = (index: number) => orderedColEntries.find(([_, value]) => value * amountOfRowsVisible - 1 > index)?.[0];

export function FilterSection<F>({ filters, form, onSubmit, onReset }: FilterSectionProps<F>) {
  const [filtersExpanded, toggleFitlersExpanded] = useToggle();

  const canExpandFilters = filters.length > minFiltersForExpansion;

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <SimpleGrid cols={cols} verticalSpacing="xs">
        {filters.map((filter, index) =>
          cloneElement(filter, {
            visibleFrom: !canExpandFilters || filtersExpanded ? undefined : filterVisibleFrom(index),
          }),
        )}
        {canExpandFilters && (
          <Flex align="flex-end" hiddenFrom={filterVisibleFrom(filters.length - 1)}>
            <Button onClick={() => toggleFitlersExpanded()} flex={1} variant="outline">
              {filtersExpanded ? "Weniger anzeigen" : "Mehr anzeigen"}
            </Button>
          </Flex>
        )}
        <Group align="flex-end">
          <Button type="submit" flex={1} leftSection={<IconFilter />}>
            Filtern
          </Button>
          <ActionIcon onClick={onReset} variant="outline" color="zli-orange" size={36}>
            <IconFilterX />
          </ActionIcon>
        </Group>
      </SimpleGrid>
    </form>
  );
}
